// Single source of truth for route classification across AppShell + Providers.
//
// Three buckets:
//   BARE       — full-bleed setup flows (no nav, no escape paths). Today
//                that's just /onboarding. Pre-auth routes that own their
//                own UI and must NOT be wrapped in <Authenticator>.
//   MARKETING  — public-facing surfaces. The landing page at "/" plus
//                the auth pages at /login and /signup. Navy MarketingTopbar
//                + bone bg + trust strip footer.
//   APP        — everything else. Authenticated workspace. Sidebar +
//                navy Footer. Wrapped in <Authenticator> in Providers.tsx.
//
// "/" needs EXACT match — a startsWith("/") check matches every path and
// would collapse the auth boundary, making every route public (Codex
// finding #1, plan-eng-review 2026-04-30). Other prefixes use exact-or-
// child-segment match (`pathname === p || pathname.startsWith(p + "/")`)
// so /login-evil resolves to APP, not MARKETING (defense against routing
// quirks even though Next.js wouldn't actually route it).
//
// Adding a new public route:
//   1. Add it to BARE_PREFIX or MARKETING_PREFIX below.
//   2. classifyRoute() and isPublicAuthRoute() pick it up automatically.
//   3. Add a test in route-classes.test.ts.

export const BARE_PREFIX: readonly string[] = ["/onboarding", "/accept-invite"];

export const MARKETING_PREFIX: readonly string[] = [
  "/login",
  "/signup",
  "/contact",
  "/privacy",
  "/cookies",
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

export type RouteClass = "bare" | "marketing" | "app";

export function classifyRoute(pathname: string): RouteClass {
  if (BARE_PREFIX.some((p) => matchesPrefix(pathname, p))) return "bare";
  if (pathname === "/") return "marketing";
  if (MARKETING_PREFIX.some((p) => matchesPrefix(pathname, p))) {
    return "marketing";
  }
  return "app";
}

// Used by Providers.tsx to decide whether to wrap children in
// <Authenticator>. BARE + MARKETING routes own their own auth UI (or
// have none); APP routes get the Cognito wrapper.
export function isPublicAuthRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  return classifyRoute(pathname) !== "app";
}
